import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { api, type AuthUser } from '@/lib/api';

/**
 * Tente une session-restoration au mount en appelant /auth/refresh
 * (le cookie httpOnly est envoyé automatiquement par credentials: include).
 */
export function useAuthBootstrap(): void {
  const setSession = useAuthStore((s) => s.setSession);
  const clearSession = useAuthStore((s) => s.clearSession);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/refresh`,
          { method: 'POST', credentials: 'include' },
        );
        if (cancelled) return;
        if (res.ok) {
          const data = (await res.json()) as { user: AuthUser; accessToken: string };
          setSession(data.user, data.accessToken);
        } else {
          clearSession();
        }
      } catch {
        if (!cancelled) clearSession();
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [setSession, clearSession]);
}

export async function performLogin(email: string, password: string): Promise<void> {
  const data = await api.post<{ user: AuthUser; accessToken: string }>(
    '/auth/login',
    { email, password },
    { skipAuth: true },
  );
  useAuthStore.getState().setSession(data.user, data.accessToken);
}

export async function performLogout(): Promise<void> {
  try {
    await api.post<void>('/auth/logout');
  } finally {
    useAuthStore.getState().clearSession();
  }
}
